import {
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'

import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { useConversationStore } from '../core/store/useConversationStore'
import { theme } from '@/Atomic/assets/theme'
import { Icon } from '@/Atomic/Atoms/Icon'

// --- Styled components ---

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${theme.space.m} ${theme.space.l};
  border: 1px solid ${theme.colors.border.stroke};
  border-radius: ${theme.radii.md};
  background: ${theme.colors.background.default};
  transition: border-color 0.15s ease;
  flex-shrink: 0;
  overflow: hidden;

  &:focus-within {
    border-color: ${theme.colors.border.active};
  }
`

const SendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: ${theme.radii.sm};
  cursor: pointer;
  background-color: ${theme.colors.primary.main};
  transition: background-color 0.15s ease;

  &:disabled {
    background-color: ${theme.colors.background.subtle};
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: ${theme.colors.primary.hover};
  }
`

const StopButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: ${theme.radii.sm};
  cursor: pointer;
  background-color: ${theme.colors.danger.main};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${theme.colors.danger.hover};
  }
`

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${theme.space.xs};
  padding: ${theme.space.xs};
`

const GrowTextarea = styled.textarea`
  flex: 1;
  resize: none;
  border: none;
  background: transparent;
  outline: none;
  padding: 4px;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.5;
  color: ${theme.colors.text.primary};
  overflow-y: hidden;
  scrollbar-gutter: stable;

  &::placeholder {
    color: ${theme.colors.text.disabled};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

// --- Constants ---

const LINE_HEIGHT_PX = 21 // 0.875rem × 1.5 at 16px base
const MAX_ROWS = 4
const MAX_PASTE_CHARS = 3000

// --- Component ---

export function ChatInput() {
  const { t } = useTranslation()
  const isProcessing = useConversationStore((s) => s.isProcessing)
  const pendingToolCalls = useConversationStore((s) => s.pendingToolCalls)
  const {
    submit: submitMessage,
    stop,
    resolveToolCall,
  } = useConversationStore.getState()
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const prevIsProcessingRef = useRef(false)
  // Holds a message submitted while a confirmation was being cancelled.
  // The cancellation resolves the tool-pause promise but the current turn's
  // finally block hasn't run yet — isProcessing is still true so
  // processUserMessage would be ignored. We queue the message here and flush
  // it as soon as isProcessing drops to false.
  const pendingSubmitRef = useRef<string | null>(null)

  // The input is blocked only when processing with no pending confirmation.
  // When a ConfirmationCard is awaiting the user, typing is allowed — submitting
  // will auto-cancel the pending confirmation before starting the new turn.
  const hasPendingConfirm = pendingToolCalls.some((p) => p.type === 'confirm')
  const inputBlocked = isProcessing && !hasPendingConfirm

  // Focus on mount
  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  // Flush a queued message once the previous turn finishes, and re-focus.
  useEffect(() => {
    if (prevIsProcessingRef.current && !isProcessing) {
      const queued = pendingSubmitRef.current
      if (queued !== null) {
        pendingSubmitRef.current = null
        submitMessage(queued)
      } else if (pendingToolCalls.length === 0) {
        textareaRef.current?.focus()
      }
    }
    prevIsProcessingRef.current = isProcessing
  }, [isProcessing, pendingToolCalls.length, submitMessage])

  const adjustHeight = () => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    const maxHeight = LINE_HEIGHT_PX * MAX_ROWS + 8
    const clamped = Math.min(el.scrollHeight, maxHeight)
    el.style.height = `${clamped}px`
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
    adjustHeight()
  }

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const text = e.clipboardData.getData('text')
    if (!text) return
    e.preventDefault()
    const el = textareaRef.current
    const truncated = text.slice(0, MAX_PASTE_CHARS)
    const start = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? value.length
    const next = value.slice(0, start) + truncated + value.slice(end)
    setValue(next)
    requestAnimationFrame(() => {
      if (el) {
        const cursor = start + truncated.length
        el.setSelectionRange(cursor, cursor)
        adjustHeight()
      }
    })
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  const submit = () => {
    const trimmed = value.trim()
    if (!trimmed || inputBlocked) return
    const hasPendingCancels = pendingToolCalls.some((p) => p.type === 'confirm')
    // Auto-cancel any pending confirmation cards
    for (const pending of pendingToolCalls) {
      if (pending.type === 'confirm') {
        resolveToolCall(pending.toolCallId, false)
      }
    }
    setValue('')
    if (hasPendingCancels) {
      // The cancelled confirmation resolves a promise inside the still-running
      // turn. Queue the message — it will be flushed once isProcessing → false.
      pendingSubmitRef.current = trimmed
    } else {
      submitMessage(trimmed)
    }
    requestAnimationFrame(() => {
      const el = textareaRef.current
      if (el) {
        el.style.height = 'auto'
        el.style.overflowY = 'hidden'
      }
    })
  }

  const canSend = value.trim().length > 0 && !inputBlocked

  return (
    <InputWrapper>
      <InputRow>
        <GrowTextarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={inputBlocked}
          placeholder={t('components.aiChatBot.inputPlaceholder')}
          rows={1}
          aria-label={t('components.aiChatBot.inputPlaceholder')}
        />
        {isProcessing && !hasPendingConfirm ? (
          <StopButton
            type="button"
            onClick={stop}
            aria-label={t('components.aiChatBot.stop')}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
              <rect width="10" height="10" rx="1" fill="white" />
            </svg>
          </StopButton>
        ) : (
          <SendButton
            onClick={submit}
            disabled={!canSend}
            type="button"
            aria-label={t('components.aiChatBot.send')}
          >
            <Icon icon="send" size="sm" color="white" />
          </SendButton>
        )}
      </InputRow>
    </InputWrapper>
  )
}
